import { Service } from 'modules/services';
import { Unit } from 'modules/units';
import React, { createContext, useContext, useMemo, useState } from 'react';

interface ProposalAICreatedCatalogContextValue {
  markNewlyCreatedService: (service: Service) => void;
  markNewlyCreatedUnit: (unit: Unit) => void;
  isNewlyCreatedService: (serviceId?: string | null) => boolean;
  isNewlyCreatedUnit: (unitId?: string | null) => boolean;
}

const defaultContextValue: ProposalAICreatedCatalogContextValue = {
  markNewlyCreatedService: () => undefined,
  markNewlyCreatedUnit: () => undefined,
  isNewlyCreatedService: () => false,
  isNewlyCreatedUnit: () => false,
};

const ProposalAICreatedCatalogContext =
  createContext<ProposalAICreatedCatalogContextValue>(defaultContextValue);

export const ProposalAICreatedCatalogProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [newlyCreatedServiceIds, setNewlyCreatedServiceIds] = useState<string[]>(
    [],
  );
  const [newlyCreatedUnitIds, setNewlyCreatedUnitIds] = useState<string[]>([]);

  const value = useMemo<ProposalAICreatedCatalogContextValue>(
    () => ({
      markNewlyCreatedService(service) {
        if (!service.id) return;

        setNewlyCreatedServiceIds((currentIds) =>
          currentIds.includes(service.id!)
            ? currentIds
            : [...currentIds, service.id!],
        );
      },
      markNewlyCreatedUnit(unit) {
        if (!unit.id) return;

        setNewlyCreatedUnitIds((currentIds) =>
          currentIds.includes(unit.id!) ? currentIds : [...currentIds, unit.id!],
        );
      },
      isNewlyCreatedService(serviceId) {
        if (!serviceId) return false;

        return newlyCreatedServiceIds.includes(serviceId);
      },
      isNewlyCreatedUnit(unitId) {
        if (!unitId) return false;

        return newlyCreatedUnitIds.includes(unitId);
      },
    }),
    [newlyCreatedServiceIds, newlyCreatedUnitIds],
  );

  return (
    <ProposalAICreatedCatalogContext.Provider value={value}>
      {children}
    </ProposalAICreatedCatalogContext.Provider>
  );
};

export function useProposalAICreatedCatalog() {
  return useContext(ProposalAICreatedCatalogContext);
}
